/**
 * WorkflowDetail - right-panel tab shell (General / Config / Settings).
 *
 * Fetches the single-workflow endpoint so changes (states, actions,
 * metadata) round-trip without invalidating the entire list query.
 * Each tab owns its own form and mutation lifecycle - the parent just
 * provides the workflow object.
 */

import { AlertTriangle, Loader2 } from 'lucide-react'
import { useState } from 'react'

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import { Badge } from '@/shared/components/ui/badge'
import { useWorkflowDetail } from '@/admin/hooks/use-workflow-definitions'

import { WorkflowGeneralTab } from './workflow-general-tab'
import { WorkflowConfigTab } from './workflow-config-tab'
import { WorkflowSettingsTab } from './workflow-settings-tab'
import { formatResourceType } from './format-resource-type'

interface WorkflowDetailProps {
  workflowUuid: string
}

type DetailTab = 'general' | 'config' | 'settings'

export function WorkflowDetail({ workflowUuid }: WorkflowDetailProps) {
  const { data: workflow, isLoading, isError } = useWorkflowDetail(workflowUuid)
  const [tab, setTab] = useState<DetailTab>('general')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-[var(--ink-3)]" size={18} />
      </div>
    )
  }

  if (isError || !workflow) {
    return (
      <div className="flex items-center gap-2 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-3 text-[12px] text-[var(--ink-3)]">
        <AlertTriangle size={14} />
        Workflow not found or failed to load.
      </div>
    )
  }

  const version = workflow.current_version
  const seeded = Boolean(
    (workflow.metadata as Record<string, unknown> | null)?.seeded,
  )

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-[18px] font-[400] text-[var(--ink)]">
              {workflow.label}
            </h2>
            {!workflow.is_active && (
              <Badge variant="secondary" className="text-[10px] uppercase">
                inactive
              </Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-[var(--ink-3)]">
            <Badge
              variant="outline"
              className="font-mono text-[10px] text-[var(--ink-2)]"
            >
              {workflow.key}
            </Badge>
            {workflow.resource_type && (
              <Badge variant="outline" className="text-[10px] uppercase">
                {formatResourceType(workflow.resource_type)}
              </Badge>
            )}
            {version && (
              <span className="text-[11px]">
                v{version.version_number}
                {version.published_at && ' · published'}
              </span>
            )}
          </div>
        </div>
      </header>

      {seeded && (
        <div className="flex items-start gap-2 rounded-[6px] border-[0.5px] border-[var(--orange)] bg-[var(--orange-bg-subtle)] px-3 py-2 text-[11px] text-[var(--ink-2)]">
          <AlertTriangle size={13} className="mt-[2px] text-[var(--orange)]" />
          <p>
            This workflow was created by a seed command. Re-running the seed
            will overwrite edits you make here.
          </p>
        </div>
      )}

      <Tabs
        value={tab}
        onValueChange={(next) => setTab(next as DetailTab)}
        className="gap-4"
      >
        <TabsList variant="line" className="border-b-[0.5px] border-b-[var(--rule)]">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="config">Config</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <WorkflowGeneralTab workflow={workflow} />
        </TabsContent>
        <TabsContent value="config">
          <WorkflowConfigTab workflow={workflow} />
        </TabsContent>
        <TabsContent value="settings">
          <WorkflowSettingsTab workflow={workflow} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
